<template>
    <div ref="test">
        <el-cascader
            ref="area32"
            v-model="value"
            :class="classObj"
            :options="options"
            :disabled="disabled ? true : isDisabledExtraParam ? false : mode!=='edit'"
            filterable
            change-on-select
            style="width: 100%"
            placeholder="请选择或输入搜索"
        />
    </div>
</template>

<script>
import emitter from 'utils/emitter';
import { TreeNode } from './Tree';

export default {
    componentName: 'validate-input',
    mixins: [emitter],
    props: {
        index: {
            type: [String, Number],
        },
        areaId: {
            type: [String, Number],
        },

        areaType: {
            type: [String, Number, Object],
        },

        cid: {
            type: [String, Number],
        },
        mode: {
            type: String,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        // 额外引入一个参数，用来扩展是否可编辑，目前毫无扩展可言
        isDisabledExtraParam: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            options: [],
            inlandAreaList: [],
            inlandRoot: new TreeNode({
                areaId: 0,
                areaName: '中国大陆',
                areaType: 0,
                extra: 'inland',
            }),
            outlandRoot: new TreeNode({
                areaId: 0,
                areaName: '国际及港澳台',
                areaType: 0,
                extra: 'outland',
            }),
            noAreaRoot: new TreeNode({
                areaName: '中国(详见包装)',
                areaId: -1,
                areaType: -1,
            }),
            isError: false,
            value: [],
        };
    },
    computed: {
        classObj() {
            return { error: this.isError, 'show-text': this.isDisabledExtraParam ? false : this.mode !== 'edit' };
        },
    },
    watch: {
        value(newValue) {
            let areaName = '';
            if (newValue.length) {
                const lastArea = newValue[newValue.length - 1];
                const areaInfo = lastArea.split('_');
                this.extra = areaInfo[2];
                this.isError = +areaInfo[0] === 0;
                if (this.isError) {
                    this.$message.error(
                        '内陆地区需选择到省级, 海外地区需选择到国家或地区',
                    );
                }
                const areaId = areaInfo[0];
                const areaType = +areaInfo[1];
                if (this.areaType !== areaType || +this.areaId !== +areaId) {
                    if (newValue[0] === '0__inland' && areaId) {
                        this.inlandAreaList.forEach((land) => {
                            if (land.areaId === areaId && land.areaType === areaType) {
                                areaName = land.areaName;
                            }
                        });
                    }
                    if (newValue[0] === '0__outland' && areaId) {
                        this.outlandAreaList.forEach((land) => {
                            if (land.areaId === areaId && land.areaType === areaType) {
                                areaName = land.areaName;
                            }
                        });
                    }
                    this.$emit('input', areaId, areaType || null, this.cid, areaName);
                }
                if (areaType === -1 && +areaId === -1) {
                    this.$emit('input', areaId, areaType || null, this.cid, this.noAreaRoot.label);
                }
            } else {
                this.$emit('input', '', '', this.cid, areaName);
            }
        },
        // 变化时重新构造选中值
        areaId() {
            this.value = this.getValue();
        },
        areaType() {
            this.value = this.getValue();
        },
    },
    created() {
        this.constructOptions();
        this.$on('validate', this.broadcastValidate);
    },
    mounted() {
        this.currentLocalLabels = this.$refs.area32.currentLabels;
    },
    methods: {
        broadcastValidate() {
            let errorMsg = '';

            if (this.areaId === '') {
                errorMsg = '产地不可为空';
            } else if (+this.areaId === 0) {
                errorMsg = '产地选择错误';
            }
            this.isError = errorMsg !== '';
            this.dispatch('validate-form', 'validate-message', errorMsg);
        },
        // eslint-disable-next-line consistent-return
        constructSelectOption(areaId, areaType) {
            areaType = +areaType;
            areaId = +areaId;
            if (areaType === -1) {
                // 选择了产地为详见包装
                return [this.noAreaRoot.value];
            }
            if (areaType === 2) {
                // 判断该节点位于国内列表,还是国外列表
                if (this.outlandAreaList && this.outlandAreaList.length) {
                    const isOutland = this.outlandAreaList.some(
                        (item) => +item.areaId === areaId,
                    );

                    // 产地为国际
                    return [
                        isOutland
                            ? this.outlandRoot.value
                            : this.inlandRoot.value,
                        `${areaId}_${areaType}`,
                    ];
                }
            }
            if (areaType === 1) {
                if (this.inlandAreaList && this.inlandAreaList.length) {
                    // 产地为城市
                    const parentAreaId = this.inlandAreaList.find((item) => +item.areaId === areaId && +item.areaType === areaType).parentAreaId;
                    // 城市的上一级类型默认为 1
                    return [
                        this.inlandRoot.value,
                        `${parentAreaId}_2`,
                        `${areaId}_${areaType}`,
                    ];
                }
            }
        },
        constructOptions() {
            this.options = [this.inlandRoot, this.outlandRoot, this.noAreaRoot];
            this.getCityInfo()
                .then(({ inlandAreaList, outlandAreaList }) => {
                    this.inlandAreaList = inlandAreaList;
                    this.outlandAreaList = outlandAreaList;

                    this.insertToTree(this.inlandRoot, inlandAreaList);
                    this.insertToTree(this.outlandRoot, outlandAreaList);

                    this.options = this.options.concat([]);
                    this.value = this.getValue();
                })
                .catch((err) => {
                    this.$message.error(err.message || '获取区域信息失败');
                });
        },

        insertToTree(root, list) {
            const children = list.filter(
                (item) => +item.parentAreaId === root.id && item.parentType === root.areaType,
            );
            if (children.length) {
                root.children = children.map((item) => new TreeNode(item));
                root.children.forEach((subRoot) => {
                    this.insertToTree(subRoot, list);
                });
            }
        },

        getCityInfo() {
            // 获取城市信息, 判断localStorage中是否有值，没有的话,通过请求获取
            return Promise.resolve().then(() => {
                const cityInfo = localStorage.getItem('city_info');
                if (cityInfo) {
                    return JSON.parse(cityInfo);
                }
                return this.$service.getAllCityInfo().then((data) => {
                    localStorage.setItem('city_info', JSON.stringify(data));
                    return data;
                });
            });
        },

        getValue() {
            if (this.areaId && +this.areaId) {
                return this.constructSelectOption(this.areaId, this.areaType);
            }
            if (this.extra === 'inland') {
                return [this.inlandRoot.value];
            }
            if (this.extra === 'outland') {
                return [this.outlandRoot.value];
            }
            return [];
        },
    },
};
</script>

<style lang="scss" scoped>
.el-cascader.error {
    .el-input input {
        border-color: #ff4949;
    }
}
.show-text {
    /deep/ .el-input span {
        display: none !important;
    }
    /deep/ input {
        background: #fff !important;
        border: none !important;
    }
    /deep/ span {
        color: black !important;
    }
}
.title{
    color: #95989E;
    font-size: 12px;
}
</style>
